<template>
  <div
    v-click-outside="hideListOptions"
    :class="[sizeClass, widthClass, {'smart-input_one-row': oneRow}, {'smart-input_select-disabled': disabled}]"
    class="smart-input smart-input_select"
  >
    <div v-if="caption" class="smart-input__name">
      {{ caption }}
    </div>
    <div class="smart-input__wrapper">
      <!--
      @slot Слот для отображения кнопки внутри SmartSelect.
      -->
      <slot name="button" />

      <div
        ref="selectToggle"
        class="smart-input__selected"
        tabindex="-1"
        @click="showListOptions"
        @keydown="showListOptions"
      >
        <div class="smart-input__selected-option">
          <div v-if="selectedOption" class="smart-input__selected-value">
            <!--
            @slot Слот для отображения элемента списка выпадающего меню по собственному шаблону.
            -->
            <slot :option="selectedOption" name="option">
              <span v-html="selectedOption.label" />
            </slot>
          </div>
          <div v-show="!selectedOption" class="smart-input__selected-value" v-text="placeholder" />
          <!-- Скрытый input для поддержания фокуса компонента. -->
          <input
            ref="selectedInput"
            autocomplete="off"
            class="smart-input__selected-input"
            type="text"
            @keydown.stop="keyDownListOptions"
          >
        </div>
      </div>

      <div class="smart-input__select-list">
        <div v-if="$slots.title" class="smart-input__select-title">
          <!--
          @slot Слот для отображения заголовка внутри выпадающего меню.
          -->
          <slot name="title" />
        </div>

        <div ref="selectList" class="smart-input__wrapper-list">
          <div
            v-for="(option, currentOptionIndex) in parsedOptions"
            :key="option.key"
            :class="{'smart-input__select-option_focused': activeOptionIndex === currentOptionIndex}"
            class="smart-input__select-option"
            @click="selectOption(option.key)"
            @mouseenter="activeOptionIndex = currentOptionIndex"
            @mouseleave="activeOptionIndex = -1"
            @mouseover="activeOptionIndex = currentOptionIndex"
          >
            <!--
            @slot Слот для отображения элемента списка выпадающего меню по собственному шаблону.
            -->
            <slot :option="option" name="option">
              <span v-html="option.label" />
            </slot>
          </div>
        </div>

        <div v-if="isEmptyParsedOptions" class="smart-input__select-option" @click="hideListOptions()">
          {{ emptyValue }}
        </div>
      </div>
    </div>
    <div v-if="error" class="smart-input__error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>/**
 * Внутренний класс для представления информации об опции для выбора в выпадающем списке.
 */

class SmartOption {
  constructor(key, label, source = null) {
    this.key = key;
    this.label = label;
    this.source = source;
  }
}

/**
 * Компонент отображения кастомного выпадающего списка.
 * В компонент передается модель данных через v-model, с которой будет устанавливаться связь.
 * Так же передается объект со списком опций и текст для невыбранного значения.
 *
 * В качестве возможных опций можно передать простой объект с текстовыми ключами и значениями,
 * можно передать массив из сложных объектов и дополнительно указать наименования полей, которые будут использоваться
 * для отображения значения и для передачи значения.
 */
export default {
  props: {
    /**
     * Значение, которое надо передавать через v-model.
     */
    value: {
      type: [String, Number],
      default: "",
    },
    /**
     * Заголовок поля.
     */
    caption: {
      type: String,
      default: "",
    },
    /**
     * Содержимое для placeholder - используется в качестве неустановленного значения.
     */
    placeholder: {
      type: String,
      default() {
        return this.$t('selectFromList');
      },
    },
    /**
     * Вариант размера элемента.
     *
     * @values s, m, l
     */
    size: {
      type: String,
      default: "m",
    },
    /**
     * Вариант ширины элемента.
     *
     * @values fix, fill
     */
    width: {
      type: String,
      default: "fix",
    },
    /**
     * Значение на случай когда отсутствуют свойства в выпадающем списке.
     */
    emptyValue: {
      type: String,
      default: "-пусто-",
    },
    /**
     * Флаг для размещения подписи и поля ввода на одной строке.
     */
    oneRow: {
      type: Boolean,
      default: false,
    },
    /**
     * Текст ошибки для поля.
     */
    error: {
      type: String,
      default: "",
    },
    /**
     * Опция для отключения возможности выбора элемента из выпадающего списка.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Набор опций для выпадающего списка.
     * Допускается передача плоского массива с простыми значениями, массива с объектами одинаковой структуры
     * (для обработки понадобится указать valueField, labelField, sortField) или простой объект вида ключ-значение.
     */
    options: {
      type: [Object, Array],
      default: () => ([]),
    },
    /**
     * Название поля в списке options, содержимое которого будет передаваться в качестве значения при выборе.
     */
    valueField: {
      type: String,
      default: null,
    },
    /**
     * Название поля в списке options, содержимое которого будет использоваться как название опции.
     */
    labelField: {
      type: String,
      default: null,
    },
    /**
     * Название поля в списке options, содержимое которого будет использоваться как ключ сортировки.
     */
    sortField: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      newValue: this.value,
      /**
       * Индекс текущего выбранного пункта в выпадающем списке.
       */
      activeOptionIndex: -1,
    };
  },
  computed: {
    /**
     * @return {Array<SmartOption>} Массив обработанных опций, пригодных к отображению в шаблоне в виде выпадающего списка.
     */
    parsedOptions() {
      let rawOptions = this.options;

      if (_.isPlainObject(rawOptions)) {
        if (!this.valueField || !this.labelField) {
          // Если это плоский объект (ключ-значение).
          return Object.entries(rawOptions).map(rawOption => new SmartOption(...rawOption));
        }
        // Плоский объект преобразуется в массив его значений, т.к. для его преобразования заданы настройки.
        rawOptions = Object.values(rawOptions);
      }

      if (Array.isArray(rawOptions)) {
        // Если в опциях массив из объектов, которые надо отсортировать и извлечь полезные данные.
        if (this.valueField && this.labelField && rawOptions.every(_.isPlainObject)) {
          let chain = _.chain(rawOptions);
          if (this.sortField) {
            chain = chain.sortBy(this.sortField);
          }
          return chain.map(rawOption => new SmartOption(rawOption[this.valueField], rawOption[this.labelField], rawOption)).value();
        }
        // Если массив опций состоит из простых значений.
        return rawOptions.map(rawOption => new SmartOption(rawOption, rawOption));
      }

      // Если опции не удалось обработать вменяемым образом.
      return [];
    },
    /**
     * @return {Object<String|Number, SmartOption>} Плоский объект вида ключ-объект опции, построенный из уже обработанных опций.
     */
    optionsByKey() {
      return _.chain(this.parsedOptions).keyBy("key").value();
    },
    /**
     * @return {SmartOption|null} Объект выбранной опции.
     */
    selectedOption() {
      return _.get(this.optionsByKey, this.newValue, null);
    },
    /**
     * @return {String} Вернуть класс, применяемый для размера отступов select.
     */
    sizeClass() {
      const sizeClasses = {
        "l": "smart-input_large",
        "m": "smart-input_medium",
        "s": "smart-input_small",
      };
      return _.get(sizeClasses, this.size, "smart-input_medium");
    },
    /**
     * @return {String} Вернуть класс, применяемый для ширины select.
     */
    widthClass() {
      const widthClasses = {
        "fix": "smart-input_select-fix",
        "fill": "smart-input_select-fill",
      };
      return _.get(widthClasses, this.width, "fix");
    },
    /**
     * @return {Boolean} Признак наличия свойств в выпадающем списке.
     */
    isEmptyParsedOptions() {
      return _.isEmpty(this.parsedOptions);
    },
  },
  watch: {
    /**
     * Наблюдение за оригинальным значением для его синхронизации со внутренним значением компонента и отправкой события `@change`.
     *
     * @param {String} val
     */
    value(val) {
      this.newValue = val;
      /**
       * Срабатывает при изменении значения со стороны родительского компонента.
       * @event change
       */
      this.$emit("change", val);
    },
  },
  methods: {
    /**
     * Метод для обработки событий, завязанных на конкретные клавиши.
     * Перехватываются все нажатия на скрытый input, поскольку для компонента важно сохранить фокус.
     * А поскольку input скрыт, то нажатия символов не должно приводить к вводу.
     *
     * @param {KeyboardEvent} event
     */
    keyDownListOptions(event) {
      const handlers = {
        // enter
        13: () => this.selectOptionByKeyEnter(),
        // esc
        27: () => this.hideListOptions(),
        // up
        38: () => this.changeFocusByKeyUp(),
        // down
        40: () => this.changeFocusByKeyDown(),
      };

      if (event.keyCode in handlers) {
        handlers[event.keyCode]();
      }
    },
    /**
     * Метод срабатывает при клике на опцию в списке.
     * Новое значение сохраняется в компоненте и передается в родительский через `@input`, в конце список скрывается.
     *
     * @param {String} newValue
     */
    selectOption(newValue) {
      this.newValue = newValue;
      /**
       * Срабатывает при выборе значения из списка.
       * @event input
       */
      this.$emit("input", this.newValue);
      this.hideListOptions();
    },
    /**
     * Метод вызывается по событию клика в поле для открытия списка.
     * Переключает видимость списка.
     */
    showListOptions() {
      if (this.disabled) {
        return;
      }

      this.$el.classList.toggle("smart-input_select-active");
      if (this.$el.classList.contains("smart-input_select-active")) {
        /**
         * Важно установить фокус на input, так как это позволит избежать поведения,
         * при котором в случае прокрутки содержимого списку опций одновременно
         * будет прокручиваться и сама страница.
         */
        this.$refs.selectedInput.focus();
        this.changeClassOption();
      }
    },
    /**
     * Метод скрывает список опций.
     * Его следует передавать как аргумент в директиву, что отслеживает клики за пределами элемента, в котором она указана.
     * Это нужно чтобы список скрывался при клике за его пределами.
     */
    hideListOptions() {
      this.$el.classList.remove("smart-input_select-active");
      this.$refs.selectedInput.blur();
    },
    /**
     * Выбор опции из выпадающего списка по нажатию на клавишу Enter.
     */
    selectOptionByKeyEnter() {
      this.selectOption(this.parsedOptions[this.activeOptionIndex].key);
      this.$refs.selectToggle.focus();
    },
    /**
     * Срабатывает при нажатии клавиши `Стрелка вниз`.
     */
    changeFocusByKeyDown() {
      if (this.activeOptionIndex < this.parsedOptions.length - 1) {
        this.activeOptionIndex++;
      } else {
        this.activeOptionIndex = -1;
      }

      this.changeClassOption();
    },
    /**
     * Срабатывает при нажатии клавиши `Стрелка вверх`.
     */
    changeFocusByKeyUp() {
      if (this.activeOptionIndex > 0) {
        this.activeOptionIndex--;
      } else if (this.activeOptionIndex === -1) {
        this.activeOptionIndex = this.parsedOptions.length - 1;
      } else {
        this.activeOptionIndex = -1;
      }

      this.changeClassOption();
    },
    /**
     * При переходе к от опции A к опции B прокручиваем содержимое списка selectList к началу координат опции B.
     */
    changeClassOption() {
      this.$refs.selectList.scrollTo(0, this.$refs.selectList.children[Math.max(this.activeOptionIndex, 0)].offsetTop - (this.$slots.title ? 36 : 0));
    },
  },
};
</script>

<style lang="scss">
@use "./scss/style.scss";
</style>
